import React, { useState, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import {
  useMediaQuery,
  VideoPlayerPauseIcon,
  VideoPlayerPlayIcon,
  VideoPlayerSoundOffIcon,
  VideoPlayerSoundOnIcon
} from '@ag1/ui';
import * as styles from './VideoPlayer.css';

type VideoPlayerProps = {
  theme: 'light' | 'dark';
  videoRef: React.RefObject<HTMLVideoElement | null>;
  initialIsMuted: boolean;
  hideVolumeButton?: boolean;
};

const IconButton = ({
  ariaLabel,
  children,
  onClick,
  className,
  theme,
  ...props
}: {
  ariaLabel: string;
  children: React.ReactNode;
  className?: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  theme: 'light' | 'dark';
}) => {
  return (
    <button
      className={clsx(styles.iconButton, className, {
        [styles.iconButtonVariants.dark]: theme === 'dark'
      })}
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      {...props}>
      {children}
    </button>
  );
};

const VideoPlayer = ({ videoRef, theme, initialIsMuted, hideVolumeButton }: VideoPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(initialIsMuted);
  const aboveSmall = useMediaQuery('s');

  const icons = {
    play: <VideoPlayerPlayIcon size={aboveSmall ? 62 : 46} />,
    pause: <VideoPlayerPauseIcon size={aboveSmall ? 62 : 46} />,
    soundOn: <VideoPlayerSoundOnIcon size={aboveSmall ? 62 : 46} />,
    soundOff: <VideoPlayerSoundOffIcon size={aboveSmall ? 62 : 46} />
  };

  const handlePlayPause = useCallback(() => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  }, [isPlaying, videoRef]);

  const handleMuteToggle = useCallback(() => {
    if (!videoRef.current) return;

    const newMutedState = !isMuted;
    const video = videoRef.current;
    video.muted = newMutedState;
    setIsMuted(newMutedState);
  }, [isMuted, videoRef]);

  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.addEventListener('ended', handleVideoEnded);
    video.addEventListener('pause', () => {
      setIsPlaying(false);
    });
    video.addEventListener('play', () => {
      setIsPlaying(true);
    });

    // This rule shouldn't trigger for a useEffect cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      video.removeEventListener('ended', handleVideoEnded);
      video.removeEventListener('pause', () => {
        setIsPlaying(false);
      });
      video.removeEventListener('play', () => {
        setIsPlaying(true);
      });
    };
  }, [videoRef, handleVideoEnded]);

  return (
    <div
      className={clsx(styles.root)}
      onClick={handlePlayPause}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handlePlayPause();
        }
      }}
      aria-label={isPlaying ? 'Pause video' : 'Play video'}>
      <div className={styles.controls}>
        <IconButton
          theme={theme}
          className={styles.playPauseButton}
          ariaLabel={isPlaying ? 'Pause video' : 'Play video'}
          onClick={(e) => {
            e.stopPropagation();
            handlePlayPause();
          }}>
          {isPlaying ? icons.pause : icons.play}
        </IconButton>
        {!hideVolumeButton && (
          <IconButton
            theme={theme}
            className={styles.soundButton}
            ariaLabel={isMuted ? 'Unmute video' : 'Mute video'}
            onClick={(e) => {
              e.stopPropagation();
              handleMuteToggle();
            }}>
            {isMuted ? icons.soundOff : icons.soundOn}
          </IconButton>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
